import {
	Building2,
	CalendarDays,
	ChevronLeft,
	ChevronRight,
	Radio,
	Video,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	Sidebar as SidebarRoot,
	useSidebar,
} from "@/components/ui/sidebar";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAccessibleChannels } from "@/hooks/use-accessible-channels";
import { useHasSchedule } from "@/hooks/use-user-schedule";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { BroadcastBanner } from "./sidebar/broadcast-banner";
import { BroadcastControlsButton } from "./sidebar/broadcast-controls-button";
import { BroadcastStatus } from "./sidebar/broadcast-status";
import { ChannelList } from "./sidebar/channel-list";
import { UserMenu } from "./sidebar/user-menu";
import { VoiceStatus } from "./sidebar/voice-status";
import { WorkspaceSelector } from "./sidebar/workspace-selector";

const MAX_MENTION_DISPLAY = 9;

function CollapsedSidebar() {
	const { toggleSidebar } = useSidebar();
	const activeChannel = useStore(uiStore, (state) => state.activeChannel);
	const room = useStore(uiStore, (state) => state.room);
	const user = useStore(authStore, (state) => state.user);
	const broadcast = useStore(coreStore, (state) => state.broadcast);
	const navigate = useStore(uiStore, (state) => state.navigate);

	const [workspaceOpen, setWorkspaceOpen] = useState(false);
	const [channelsOpen, setChannelsOpen] = useState(false);
	const hasSchedule = useHasSchedule();
	const showVoice = !!activeChannel && !!room;
	const showBroadcast = !!broadcast && user?.uid === broadcast.startedBy;

	const channelMap = useAccessibleChannels();
	const { hasAnyUnread, totalMentions } = useMemo(() => {
		let unread = false;
		let mentions = 0;
		for (const ch of channelMap.values()) {
			if (
				ch.forcedUnreadMessageId ||
				(ch.latestMessageId &&
					(!ch.readStatus || ch.latestMessageId > ch.readStatus))
			) {
				unread = true;
			}
			if (ch.mentionCount) {
				mentions += ch.mentionCount;
			}
		}
		return { hasAnyUnread: unread, totalMentions: mentions };
	}, [channelMap]);

	return (
		<div className="flex h-full flex-col items-center gap-1 p-2">
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						className="size-9"
						onClick={toggleSidebar}
						size="icon"
						variant="ghost"
					>
						<ChevronRight className="size-4" />
					</Button>
				</TooltipTrigger>
				<TooltipContent side="right">Expand Sidebar</TooltipContent>
			</Tooltip>
			<Popover onOpenChange={setWorkspaceOpen} open={workspaceOpen}>
				<Tooltip>
					<TooltipTrigger asChild>
						<PopoverTrigger asChild>
							<Button className="size-9" size="icon" variant="ghost">
								<Building2 className="size-4" />
							</Button>
						</PopoverTrigger>
					</TooltipTrigger>
					<TooltipContent side="right">Workspace</TooltipContent>
				</Tooltip>
				<PopoverContent align="start" className="w-64 p-0" side="right">
					{workspaceOpen && (
						<>
							<WorkspaceSelector />
							<BroadcastBanner />
						</>
					)}
				</PopoverContent>
			</Popover>

			<Popover onOpenChange={setChannelsOpen} open={channelsOpen}>
				<Tooltip>
					<TooltipTrigger asChild>
						<PopoverTrigger asChild>
							<Button className="relative size-9" size="icon" variant="ghost">
								<svg
									className="size-6"
									fill="none"
									strokeLinecap="round"
									strokeLinejoin="round"
									viewBox="0 0 500 400"
									xmlns="http://www.w3.org/2000/svg"
								>
									<title>Channels</title>
									<circle
										cx="110"
										cy="250"
										r="40"
										stroke="currentColor"
										strokeWidth="20"
									/>
									<path
										d="M 50 370 C 50 320, 75 305, 95 305 Q 110 315, 125 305 C 145 305, 170 320, 170 370 Z"
										fill="none"
										stroke="currentColor"
										strokeLinejoin="round"
										strokeWidth="20"
									/>
									<circle
										cx="250"
										cy="250"
										r="40"
										stroke="currentColor"
										strokeWidth="20"
									/>
									<path
										d="M 190 370 C 190 320, 215 305, 235 305 Q 250 315, 265 305 C 285 305, 310 320, 310 370 Z"
										fill="none"
										stroke="currentColor"
										strokeLinejoin="round"
										strokeWidth="20"
									/>
									<circle
										cx="390"
										cy="250"
										r="40"
										stroke="currentColor"
										strokeWidth="20"
									/>
									<path
										d="M 330 370 C 330 320, 355 305, 375 305 Q 390 315, 405 305 C 425 305, 450 320, 450 370 Z"
										fill="none"
										stroke="currentColor"
										strokeLinejoin="round"
										strokeWidth="20"
									/>
									<path
										d="M 130 149 A 60 45 0 1 1 160 155 L 110 190 Z"
										fill="white"
										stroke="currentColor"
										strokeLinejoin="round"
										strokeWidth="20"
									/>
									<path
										d="M 340 155 A 60 45 0 1 1 370 149 L 390 190 Z"
										fill="white"
										stroke="currentColor"
										strokeLinejoin="round"
										strokeWidth="20"
									/>
									<path
										d="M 220 120 A 60 45 0 1 1 280 120 L 250 160 Z"
										fill="white"
										stroke="currentColor"
										strokeLinejoin="round"
										strokeWidth="20"
									/>
								</svg>
								{totalMentions > 0 && (
									<span className="absolute top-0 right-0.5 flex size-3.5 items-center justify-center rounded-full bg-red-500/80 text-[0.6rem] text-bold text-white">
										{totalMentions > MAX_MENTION_DISPLAY ? "9+" : totalMentions}
									</span>
								)}
								{hasAnyUnread && (
									<span className="-translate-y-1/2 -left-3.25 absolute top-1/2 size-2.5 rotate-45 rounded-xs bg-foreground" />
								)}
							</Button>
						</PopoverTrigger>
					</TooltipTrigger>
					<TooltipContent side="right">Channels</TooltipContent>
				</Tooltip>
				<PopoverContent
					align="start"
					className="h-[60vh] w-64 overflow-auto p-0"
					side="right"
				>
					{channelsOpen && <ChannelList />}
				</PopoverContent>
			</Popover>

			{hasSchedule && (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							className="size-9"
							onClick={() => navigate({ type: "schedule" })}
							size="icon"
							variant="ghost"
						>
							<CalendarDays className="size-4" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="right">Schedule</TooltipContent>
				</Tooltip>
			)}

			<div className="flex-1" />

			{showVoice && (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							className="size-9"
							onClick={() =>
								navigate({ type: "voiceChannel" }, { newTab: true })
							}
							size="icon"
							variant="ghost"
						>
							<Video className="size-4 text-green-500" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="right">
						{activeChannel.name} — Connected
					</TooltipContent>
				</Tooltip>
			)}

			<BroadcastControlsButton collapsed />

			{showBroadcast && (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							className="size-9"
							onClick={() => navigate({ type: "broadcastControls" })}
							size="icon"
							variant="ghost"
						>
							<Radio className="size-4 text-red-500" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="right">
						{broadcast.title} —{" "}
						{broadcast.status === "live" ? "Live" : "Paused"}
					</TooltipContent>
				</Tooltip>
			)}

			<UserMenu collapsed />
		</div>
	);
}

export function Sidebar() {
	const { state, toggleSidebar } = useSidebar();
	const isCollapsed = state === "collapsed";

	const channelsMap = useStore(coreStore, (s) => s.channels);
	const navigate = useStore(uiStore, (s) => s.navigate);
	const tourPreferences = useStore(coreStore, (s) => s.tourPreferences);
	const updateTourStatus = useStore(coreStore, (s) => s.updateTourStatus);

	// Workspace selection and the sequential workspace-data load live in the
	// core-store orchestrator (loadOnConnect and selectWorkspace). The old
	// auto-select and parallel fan-out effects were removed from here.

	// Auto-open channels marked openOnFirstLogin, once, with the flag on the server.
	useEffect(() => {
		if (channelsMap.size === 0) return;
		if (!tourPreferences || tourPreferences.hasSeenFirstLoginChannels) return;

		const channels = Array.from(channelsMap.values());
		const firstLoginChannels = channels.filter((c) => c?.openOnFirstLogin);

		if (firstLoginChannels.length === 0) return;

		updateTourStatus({ tourType: "firstLoginChannels", completed: true });

		for (const channel of firstLoginChannels) {
			navigate(
				{
					type: "textChannel",
					params: { channelId: channel.id },
				},
				{ newTab: true }
			);
		}
	}, [channelsMap, navigate, tourPreferences, updateTourStatus]);

	return (
		<SidebarRoot className="gap-2 border-none" collapsible="icon">
			<div className={isCollapsed ? "flex h-full flex-col" : "hidden"}>
				<CollapsedSidebar />
			</div>
			<div className={isCollapsed ? "hidden" : "contents"}>
				<SidebarHeader>
					<div className="flex min-w-0 items-center gap-2">
						<Card className="flex shrink-0 items-center py-2.5">
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										className="size-7 shrink-0"
										onClick={toggleSidebar}
										size="icon"
										variant="ghost"
									>
										<ChevronLeft className="size-4" />
									</Button>
								</TooltipTrigger>
								<TooltipContent side="right">Collapse Sidebar</TooltipContent>
							</Tooltip>
						</Card>
						<div className="min-w-0 flex-1">
							<WorkspaceSelector />
						</div>
					</div>
					<BroadcastBanner />
				</SidebarHeader>

				<SidebarContent className="flex max-h-full flex-col overflow-hidden p-2 pt-0">
					<BroadcastControlsButton />
					<ChannelList />
				</SidebarContent>

				<SidebarFooter>
					<Card className="gap-2! p-2">
						<VoiceStatus />
						<BroadcastStatus />
						<UserMenu />
					</Card>
				</SidebarFooter>
			</div>
		</SidebarRoot>
	);
}
